<script setup lang="ts">
import { Heading } from '~ds/components/ui/typography';

import type { ArticleGuideItem } from '~/utils/article';

/**
 * Guide item whose `number`, when set, is rendered as its own "N. label" instead of
 * relying on KUICoreListRenderer's position-based numbering — callers whose displayed
 * list is filtered from a larger source (e.g. steps with unslugifiable titles dropped)
 * need numbers that stay true to the unfiltered source.
 */
export interface ArticleGuideNavItem extends ArticleGuideItem {
  number?: number;
}

interface Props {
  items: ArticleGuideNavItem[];
  /** Card heading; defaults to the step template's "In this guide". */
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
});

const hasExplicitNumbers = computed(() => props.items.length > 0 && props.items.every(item => item.number !== undefined));
</script>

<template>
  <aside class="rounded-2xl bg-white p-6">
    <Heading
      size="sm"
      as="h2"
      class="mb-4"
    >
      {{ label ?? $t('unified.articles.inThisGuide') }}
    </Heading>
    <KUICoreListRenderer :variant="hasExplicitNumbers ? 'none' : 'numbered'" class="space-y-2">
      <a
        v-for="item in items"
        :key="item.id"
        :href="`#${item.id}`"
        class="text-sm text-foreground hover:underline"
      >
        <template v-if="item.number !== undefined">{{ item.number }}. </template>{{ item.label }}
      </a>
    </KUICoreListRenderer>
  </aside>
</template>
